import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Http from "../services/Http";
import { toastHandler } from "../../utils/utils";
import { messages } from "../../utils/messages";

// Initial state for the guide (training videos) slice
const initialState = {
  guideData: {
    data: [], // Initialize data as an empty array
  },
  status: "idle",
  error: null,
};

// Destructure the httpRequest function from Http service
const { httpRequest } = Http();

/**
 * Find a Laravel-style paginator object inside an arbitrary response shape.
 * Handles { data: [...] }, { training_videos: {...} }, { data: { videos: {...} } } etc.
 * @param {Object} obj - The response payload.
 * @param {number} depth - Current recursion depth.
 * @returns {Object|null} - The paginator object (with a `data` array) or null.
 */
const findPaginator = (obj, depth = 0) => {
  if (!obj || typeof obj !== "object" || depth > 3) return null;
  if (Array.isArray(obj.data)) return obj;
  for (const key of Object.keys(obj)) {
    const found = findPaginator(obj[key], depth + 1);
    if (found) return found;
  }
  return null;
};

/**
 * Async thunk for fetching the list of training videos (Guide).
 * Calls GET /admin/training-videos?page={page}.
 * @returns {Object} - Normalized as { videos: <paginator> }.
 */
export const trainingVideoList = createAsyncThunk(
  "listTrainingVideo",
  async ({ pageCount, navigate }, { rejectWithValue }) => {
    try {
      const response = await httpRequest(
        {
          method: "GET",
          url: `admin/training-videos?page=${pageCount}`,
        },
        navigate
      );
      return { videos: findPaginator(response) || { data: [] } };
    } catch (err) {
      return rejectWithValue(err.message || messages.listTrainingVideo.error, {
        cause: err.cause,
      });
    }
  }
);

// Create a slice for guide-related actions and state management
const guideSlice = createSlice({
  name: "guide",
  initialState,
  reducers: {
    /**
     * Reducer for showing toast messages.
     * @param {Object} state - The current state.
     * @param {Object} action - The action containing payload data.
     */
    showToast: (state, action) => {
      const { success, message, navigate, navigatePath, locationState } =
        action.payload;
      toastHandler(success, message, navigate, navigatePath, locationState);
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        (action) =>
          [trainingVideoList.pending].some((type) => type.match(action)),
        (state, action) => {
          state.status = "loading";
          state.error = null;
          if (action.meta.arg?.setLoader) {
            action.meta.arg.setLoader(true);
          }
        }
      )
      .addMatcher(
        (action) =>
          [trainingVideoList.fulfilled].some((type) => type.match(action)),
        (state, action) => {
          state.status = "succeeded";

          if (["listTrainingVideo"].includes(action.type.split("/")[0])) {
            const existingData = state.guideData.data;
            const newData = action.payload.videos.data || [];

            const mergedData = [...existingData, ...newData].reduce(
              (acc, current) => {
                if (!acc.find((item) => item.id === current.id)) {
                  acc.push(current);
                }
                return acc;
              },
              []
            );

            state.guideData = {
              ...action.payload.videos,
              data: mergedData,
            };
          }

          if (action.meta.arg?.setLoader) {
            action.meta.arg.setLoader(false);
          }
        }
      )
      .addMatcher(
        (action) =>
          [trainingVideoList.rejected].some((type) => type.match(action)),
        (state, action) => {
          state.status = "failed";
          state.error =
            action.payload || messages.listTrainingVideo.error;
          if (action.meta.arg?.setLoader) {
            action.meta.arg.setLoader(false);
          }
          guideSlice.caseReducers.showToast(state, {
            payload: {
              success: false,
              message: action.payload,
            },
          });
        }
      );
  },
});

export default guideSlice.reducer;
